import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import AppInputField from "../../../components/ui/AppInputField";
import { Spinner } from "../../../components/ui/common";
import { Modal } from "../../../components/ui/Modal";
import { MySelect } from "../../../components/ui/Select";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import CompanyDropdownMenu from "../../company/components/CompanyDropdownMenu";
import { Subscription } from "../@types";
import {
  useAddSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from "../subscriptionApiSlice";

export const subscriptionFormSchema = z.object({
  companyId: z.string().nonempty("Company id is required"),
  companyName: z.string().nonempty("Company name is required"),
  autoRenewal: z.boolean(),
  duration: z.string().nonempty("Duration is required"),
  maxVehicles: z.number().min(1, "Max vehicles must be greater than 0"),
  multiplier: z.number().min(1, "Multiplier must be greater than 0"),
  isCustom: z.boolean(),
  planTier: z.string().nonempty("Plan tier is required"),
});

export type SubscriptionFormData = z.infer<typeof subscriptionFormSchema>;

interface CreateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: Subscription | null;
  mode?: "add" | "edit";
}

const CreateSubscriptionModal: React.FC<CreateSubscriptionModalProps> = ({
  isOpen,
  onClose,
  plan,
  mode,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && plan;

  const [
    addSubscription,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useAddSubscriptionMutation();

  const [
    updateSubscription,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateSubscriptionMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<SubscriptionFormData>({
    resolver: zodResolver(subscriptionFormSchema),
    mode: "onChange",
    defaultValues: {
      companyId: "",
      companyName: "",
      autoRenewal: false,
      duration: "",
      maxVehicles: 0,
      multiplier: 0,
      isCustom: false,
      planTier: "",
    },
  });

  // Populate form with plan data when editing
  useEffect(() => {
    if (isEditMode && plan) {
      reset({
        companyId: plan.companyId,
        companyName: plan.companyName,
        autoRenewal: plan.autoRenewal,
        duration: plan.duration,
        maxVehicles: plan.maxVehicles,
        multiplier: plan.multiple,
        isCustom: plan.isCustom,
        planTier: plan.planTier,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        companyId: "",
        companyName: "",
        autoRenewal: false,
        duration: "",
        maxVehicles: 0,
        multiplier: 0,
        isCustom: false,
        planTier: "",
      });
    }
  }, [isEditMode, plan, reset]);

  const onSubmit = async (data: SubscriptionFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        companyId: data.companyId,
        companyName: data.companyName,
        autoRenewal: data.autoRenewal,
        duration: data.duration,
        maxVehicles: data.maxVehicles,
        multiplier: data.multiplier,
        isCustom: data.isCustom,
        planTier: data.planTier,
      };

      if (isEditMode && plan?.id) {
        await updateSubscription({
          id: plan.id,
          ...payload,
        }).unwrap();
      } else {
        await addSubscription(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } subscription. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Subscription ${
          isEditMode ? "updated" : "added"
        } successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } subscription. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Subscription has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess
    ? ""
    : `${isEditMode ? "Edit" : "Add"} subscription`;
  const submitButtonText = isEditMode
    ? "Update subscription"
    : "+ Add subscription";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white pb-8"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          <div className="">
            <Controller
              control={control}
              name="companyId"
              render={({ field }) => (
                <CompanyDropdownMenu
                  value={field.value}
                  onChange={(value) => {
                    setValue("companyId", value?.value || "", {
                      shouldValidate: true,
                    });

                    setValue("companyName", value?.label || "", {
                      shouldValidate: true,
                    });
                  }}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                  error={errors.companyId?.message}
                  placeholder="Select a company"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[13px]">Duration</label>
              <Controller
                control={control}
                name="duration"
                render={({ field }) => (
                  <MySelect
                    options={["monthly", "quarterly", "yearly"]}
                    onChange={field.onChange}
                    value={field.value}
                    name={field.name}
                    placeholder="Select duration"
                    getName={(option) => option}
                    getValue={(option) => option}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  />
                )}
              />
              {errors.duration && (
                <p className="text-primary text-sm mt-1">
                  {errors.duration.message}
                </p>
              )}
            </div>

            <div>
              <label className="text-[13px]">Duration</label>
              <Controller
                control={control}
                name="planTier"
                render={({ field }) => (
                  <MySelect
                    options={["basic", "professional"]}
                    onChange={field.onChange}
                    value={field.value}
                    name={field.name}
                    placeholder="Select plan tier"
                    getName={(option) => option}
                    getValue={(option) => option}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  />
                )}
              />
              {errors.planTier && (
                <p className="text-primary text-sm mt-1">
                  {errors.planTier.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="maxVehicles"
              render={({ field }) => (
                <AppInputField
                  label="Max vehicles"
                  type="number"
                  error={errors.maxVehicles?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter max vehicles"
                />
              )}
            />

            <Controller
              control={control}
              name="multiplier"
              render={({ field }) => (
                <AppInputField
                  label="Multiplier"
                  type="number"
                  error={errors.multiplier?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter multiplier"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Auto Renewal */}
            <label className="flex items-center gap-3 cursor-pointer">
              <Controller
                control={control}
                name="autoRenewal"
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    className="h-4 w-4 accent-blue-600"
                  />
                )}
              />
              <span className="text-sm font-medium text-gray-700">
                Auto renewal
              </span>
            </label>

            {/* Custom Plan */}
            <label className="flex items-center gap-3 cursor-pointer">
              <Controller
                control={control}
                name="isCustom"
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    className="h-4 w-4 accent-blue-600"
                  />
                )}
              />
              <span className="text-sm font-medium text-gray-700">
                Already have a custom plan?
              </span>
            </label>
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? <Spinner /> : submitButtonText}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default CreateSubscriptionModal;
